import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type User } from './api.ts';

interface AuthState {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: User | null }>('/api/auth/me');
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    setUser(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth måste användas inuti AuthProvider');
  return ctx;
}

export const isTeacher = (user: User | null) => user?.role === 'teacher' || user?.role === 'admin';
/** Rättare i någon kurs (rollen gäller per kurs, kontot kan i övrigt vara ett deltagarkonto). */
export const isGrader = (user: User | null) => !!user?.staff?.some((s) => s.role === 'grader');
/** Lärare eller rättare: når lärarsidorna, rättare bara rättning och deltagare i sina grupper. */
export const isStaff = (user: User | null) => isTeacher(user) || isGrader(user);
export const isMunicipalityContact = (user: User | null) =>
  !!user?.staff?.some((s) => s.role === 'municipality_contact');
